import { SelectedTypedInstrument, SelectedExperiment } from "helpers/types/instruments/selected"

export interface OptimizationExperimentSource {
  experimentId: string
  name: string
}

export type OptimizationSourceKind = "original" | "experiment"

/** Stable string for comparing which optimization input (portfolio + optional experiment) is selected. */
export function getOptimizationSourceIdentityKey(positionSet: SelectedTypedInstrument | null): string | null {
  if (!positionSet) {
    return null
  }
  if (positionSet.type !== "PORTFOLIO") {
    return `${positionSet.type}:${positionSet.id}`
  }
  const experimentId =
    "experimentId" in positionSet && positionSet.experimentId != null
      ? positionSet.experimentId
      : null
  return experimentId != null
    ? `PORTFOLIO:${positionSet.id}:exp:${experimentId}`
    : `PORTFOLIO:${positionSet.id}:original`
}

export function isOptimizationSourceChangedSinceLastApply(
  optimizationSourceIdentityKey: string | null,
  lastAppliedOptimizationSourceKey: string | null,
): boolean {
  if (optimizationSourceIdentityKey == null || lastAppliedOptimizationSourceKey == null) {
    return false
  }
  return lastAppliedOptimizationSourceKey !== optimizationSourceIdentityKey
}

export function getOptimizationPositionSet(
  instrument: SelectedTypedInstrument | null,
  optimizationSourceKind: OptimizationSourceKind,
  optimizationExperimentSource: OptimizationExperimentSource | null,
): SelectedTypedInstrument | null {
  if (!instrument || instrument.type !== "PORTFOLIO") {
    return instrument
  }
  if (optimizationSourceKind !== "experiment" || !optimizationExperimentSource) {
    return { type: "PORTFOLIO", id: instrument.id }
  }
  const withExperiment: SelectedExperiment = {
    type: "PORTFOLIO",
    id: instrument.id,
    experimentId: optimizationExperimentSource.experimentId,
  }
  return withExperiment
}

export function isOptimizationSourceSelectionComplete(
  optimizationSourceKind: OptimizationSourceKind,
  optimizationExperimentSource: OptimizationExperimentSource | null,
): boolean {
  if (optimizationSourceKind !== "experiment") {
    return true
  }
  return optimizationExperimentSource != null
}

export function getOptimizationResultExperimentName(
  optimizationSourceKind: OptimizationSourceKind,
  optimizationExperimentSource: OptimizationExperimentSource | null,
): string | null {
  if (optimizationSourceKind !== "experiment") {
    return null
  }
  return optimizationExperimentSource?.name ?? null
}
